import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRouteSnapshot, CanActivate, CanDeactivate, NavigationEnd, Router, RouterStateSnapshot } from '@angular/router';
import { interval, of, Subscription } from 'rxjs';
import { filter, map, switchMap, take, tap } from 'rxjs/operators';
import { AppContext } from 'src/app/app/context';
import {
  AssetLockService,
  UserRole
} from '../../services/api.services';
import { UnlockNotificationDialogComponent } from '../dialogs/unlock-notification-dialog/unlock-notification-dialog.component';
import { catchAppError } from '../utils';
import { UnsavedChanges, UnsavedChangesGuard } from './unsaved-changes.guard';
import { logger } from 'src/app/pbir/utils/logger';
import { AssetLocks } from 'src/app/services/AssetLocks.service';

const LOGGER = logger('AssetLockGuard');

export enum AssetTypes { 
  Visual = 'Visual',
  Dashboard = 'Dashboard', 
  Schedule = 'Schedule',
  PublishedReport = 'PublishedReport'
}

@Injectable({ providedIn: 'root' })
export class AssetLockGuard implements CanActivate, CanDeactivate<UnsavedChanges> {
  readonly destroyRef = inject(DestroyRef);
  readonly appContext = inject(AppContext);

  readonly assetLocksApi = inject(AssetLockService);
  readonly assetLocks    = inject(AssetLocks);

  readonly dialog = inject(MatDialog);
  readonly unsavedChangesGuard = inject(UnsavedChangesGuard);
  readonly router = inject(Router);

  private currentLock: AssetLockInfo;
  private refreshCurrentLock: Subscription;
  private lockSub: Subscription;

  canActivate(route: ActivatedRouteSnapshot) {
    let content = this.parseRoute(route);
    if (!content)
      return of(true);

    this.assetLocks.refresh();

    return this.assetLocksApi.hasLocks(content.typeName, content.id).pipe(
      map(lockDto => {
        if (lockDto && lockDto.lockedByUserId !== this.appContext.user().id) {
          this.dialog.open(UnlockNotificationDialogComponent, {
            ...UnlockNotificationDialogComponent.defaults,
            data: lockDto,
          });

          return false;
        }

        return true;
      })
    );
  }

  /**
   * Requests a lock to a piece of content.
   * The lock becomes active until `removeCurrentLock()` is called or the guard deactivated.
   * The lock will only be acquired if the user is non-admin. */
  requestLock(content: AssetLockInfo) {
    if (this.appContext.user().role == UserRole.InsightsUser) {
      this.setCurrentLock(content);
      return true;
    }

    return false;
  }

  /** 
   * Discards any active lock.
   */
  removeCurrentLock() {
    if (this.currentLock) {
      this.refreshCurrentLock?.unsubscribe();
      this.refreshCurrentLock = null;

      let lock = this.currentLock;
      this.currentLock = null;

      LOGGER.info(`Unlocking ${lock.typeName} - ${lock.id}`);

      this.assetLocksApi.removeUserLock(lock.typeName, lock.id).pipe(
        catchAppError({ showErrorSnack: false })
      ).subscribe();
    }
  }

  private setCurrentLock(content: AssetLockInfo) {
    if (this.currentLock?.id != content.id || this.currentLock?.typeName != content.typeName) {
      this.removeCurrentLock();
    }

    this.currentLock = content;

    this.lockSub?.unsubscribe();
    this.lockSub = this.upsertLock(content).pipe(
      takeUntilDestroyed(this.destroyRef),
      tap(() => this.startCurrentLockRefresh())
    ).subscribe();
  }

  private startCurrentLockRefresh() {
    this.refreshCurrentLock ??= interval(5000).pipe(
      takeUntilDestroyed(this.destroyRef),
      tap(() => {
        if (this.currentLock)
          this.upsertLock(this.currentLock).subscribe();
      }),
    ).subscribe();
  }

  private upsertLock(content: AssetLockInfo) {
    return this.assetLocksApi.upsertUserLock(content.typeName, content.id).pipe(
      takeUntilDestroyed(this.destroyRef),
      catchAppError({ showErrorSnack: false })
    );
  }

  canDeactivate(
    component: UnsavedChanges,
    currentRoute: ActivatedRouteSnapshot,
    currentState: RouterStateSnapshot,
    nextState?: RouterStateSnapshot
  ) {
    return this.unsavedChangesGuard.canDeactivate(component, currentRoute, currentState, nextState).pipe(
      map(canContinue => {
        if (!canContinue)
          return false;

        this.removeCurrentLock();
        return true;
      })
    );
  }

  private parseRoute(route: ActivatedRouteSnapshot) {
    let routeParam = route.data.routeParam;
    let typeName = route.data.contentTypeName;

    let id = parseInt(route.paramMap.get(routeParam));
    if (isNaN(id) || id < 1)
      return null;

    return { typeName, id };
  }
}

export interface AssetLockInfo {
  typeName: AssetTypes;
  id: number;
}
